import React, { useState, useEffect } from 'react';
import { Shield, Lock, Eye, EyeOff, CheckCircle, AlertTriangle, Clock, Key, Fingerprint, Smartphone } from 'lucide-react';

const AdminVerification: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [verificationData, setVerificationData] = useState({
    password: '',
    totp: '',
    biometric: false,
    hardwareKey: false
  });
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState<'pending' | 'success' | 'failed'>('pending');

  const steps = [
    {
      id: 1,
      title: 'Master Password',
      description: 'Enter your master administrative password',
      icon: Lock,
      color: 'red'
    },
    {
      id: 2,
      title: 'Two-Factor Authentication',
      description: 'Verify with your authenticator app',
      icon: Smartphone,
      color: 'blue'
    },
    {
      id: 3,
      title: 'Biometric Verification',
      description: 'Complete biometric authentication',
      icon: Fingerprint,
      color: 'green'
    }
  ];

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsVerifying(true);
    
    // Simulate password verification
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    if (verificationData.password === 'admin123') {
      setCurrentStep(2);
      setIsVerifying(false);
    } else {
      setVerificationStatus('failed');
      setIsVerifying(false);
    }
  };

  const handleTOTPSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsVerifying(true);
    
    // Simulate TOTP verification
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    if (verificationData.totp === '123456') {
      setCurrentStep(3);
      setIsVerifying(false);
    } else {
      setVerificationStatus('failed');
      setIsVerifying(false);
    }
  };

  const handleBiometricVerification = async () => {
    setIsVerifying(true);
    
    // Simulate biometric verification
    await new Promise(resolve => setTimeout(resolve, 3000));
    
    setVerificationStatus('success');
    setIsVerifying(false);
  };

  const getStepColor = (stepId: number) => {
    if (stepId < currentStep) return 'text-green-400';
    if (stepId === currentStep) return 'text-cyan-400';
    return 'text-slate-400';
  };

  const getStepBgColor = (stepId: number) => {
    if (stepId < currentStep) return 'bg-green-500/20 border-green-500/30';
    if (stepId === currentStep) return 'bg-cyan-500/20 border-cyan-500/30';
    return 'bg-slate-700/50 border-slate-600/30';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <Shield className="w-16 h-16 text-cyan-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Administrative Access</h1>
          <p className="text-slate-400">Three-step verification required for administrative functions</p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className={`flex items-center justify-center w-12 h-12 rounded-full border-2 transition-all duration-300 ${getStepBgColor(step.id)}`}>
                  {step.id < currentStep ? (
                    <CheckCircle className="w-6 h-6 text-green-400" />
                  ) : (
                    <step.icon className={`w-6 h-6 ${getStepColor(step.id)}`} />
                  )}
                </div>
                {index < steps.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-4 ${
                    step.id < currentStep ? 'bg-green-500' : 'bg-slate-600'
                  }`}></div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Verification Forms */}
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-8">
          {/* Step 1: Password */}
          {currentStep === 1 && (
            <div>
              <div className="text-center mb-6">
                <Lock className="w-12 h-12 text-red-400 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-white mb-2">Master Password</h2>
                <p className="text-slate-400">Enter your master administrative password</p>
              </div>

              <form onSubmit={handlePasswordSubmit} className="space-y-6">
                <div>
                  <label className="block text-slate-400 text-sm mb-2">Master Password</label>
                  <div className="relative">
                    <input
                      type="password"
                      value={verificationData.password}
                      onChange={(e) => setVerificationData({ ...verificationData, password: e.target.value })}
                      className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-3 pr-12 text-white focus:border-red-500 focus:outline-none"
                      placeholder="Enter master password"
                      required
                    />
                    <Lock className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                  </div>
                </div>

                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5" />
                    <div>
                      <h3 className="text-red-400 font-medium">Security Notice</h3>
                      <p className="text-slate-400 text-sm mt-1">
                        This password provides access to critical administrative functions. 
                        All access attempts are logged and monitored.
                      </p>
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isVerifying}
                  className="w-full bg-gradient-to-r from-red-500 to-red-600 text-white py-3 px-6 rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isVerifying ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Verifying...</span>
                    </div>
                  ) : (
                    'Verify Password'
                  )}
                </button>
              </form>
            </div>
          )}

          {/* Step 2: TOTP */}
          {currentStep === 2 && (
            <div>
              <div className="text-center mb-6">
                <Smartphone className="w-12 h-12 text-blue-400 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-white mb-2">Two-Factor Authentication</h2>
                <p className="text-slate-400">Enter the 6-digit code from your authenticator app</p>
              </div>

              <form onSubmit={handleTOTPSubmit} className="space-y-6">
                <div>
                  <label className="block text-slate-400 text-sm mb-2">Authentication Code</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={verificationData.totp}
                      onChange={(e) => setVerificationData({ ...verificationData, totp: e.target.value })}
                      className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-3 pr-12 text-white focus:border-blue-500 focus:outline-none text-center text-2xl tracking-widest"
                      placeholder="123456"
                      maxLength={6}
                      required
                    />
                    <Key className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                  </div>
                </div>

                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Clock className="w-5 h-5 text-blue-400 mt-0.5" />
                    <div>
                      <h3 className="text-blue-400 font-medium">Time-Sensitive</h3>
                      <p className="text-slate-400 text-sm mt-1">
                        The authentication code expires every 30 seconds. 
                        Make sure to enter the current code from your authenticator app.
                      </p>
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isVerifying}
                  className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-3 px-6 rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isVerifying ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Verifying...</span>
                    </div>
                  ) : (
                    'Verify Code'
                  )}
                </button>
              </form>
            </div>
          )}

          {/* Step 3: Biometric */}
          {currentStep === 3 && (
            <div>
              <div className="text-center mb-6">
                <Fingerprint className="w-12 h-12 text-green-400 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-white mb-2">Biometric Verification</h2>
                <p className="text-slate-400">Complete biometric authentication to access administrative functions</p>
              </div>

              <div className="space-y-6">
                <div className="text-center">
                  <div className="w-32 h-32 bg-green-500/20 border-2 border-green-500/30 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Fingerprint className="w-16 h-16 text-green-400" />
                  </div>
                  <p className="text-slate-400">Place your finger on the biometric sensor</p>
                </div>

                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Shield className="w-5 h-5 text-green-400 mt-0.5" />
                    <div>
                      <h3 className="text-green-400 font-medium">Final Security Layer</h3>
                      <p className="text-slate-400 text-sm mt-1">
                        Biometric verification ensures only authorized personnel can access 
                        critical administrative functions.
                      </p>
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleBiometricVerification}
                  disabled={isVerifying}
                  className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white py-3 px-6 rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isVerifying ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Scanning...</span>
                    </div>
                  ) : (
                    'Start Biometric Scan'
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Success State */}
          {verificationStatus === 'success' && (
            <div className="text-center">
              <div className="w-20 h-20 bg-green-500/20 border-2 border-green-500/30 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Verification Complete</h2>
              <p className="text-slate-400 mb-6">You now have full administrative access</p>
              <button
                onClick={() => window.location.href = '/admin'}
                className="bg-gradient-to-r from-green-500 to-green-600 text-white py-3 px-8 rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-200"
              >
                Access Admin Dashboard
              </button>
            </div>
          )}

          {/* Failed State */}
          {verificationStatus === 'failed' && (
            <div className="text-center">
              <div className="w-20 h-20 bg-red-500/20 border-2 border-red-500/30 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertTriangle className="w-10 h-10 text-red-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Verification Failed</h2>
              <p className="text-slate-400 mb-6">Please try again with correct credentials</p>
              <button
                onClick={() => {
                  setVerificationStatus('pending');
                  setCurrentStep(1);
                  setVerificationData({ password: '', totp: '', biometric: false, hardwareKey: false });
                }}
                className="bg-gradient-to-r from-red-500 to-red-600 text-white py-3 px-8 rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200"
              >
                Try Again
              </button>
            </div>
          )}
        </div>

        {/* Security Footer */}
        <div className="mt-8 text-center">
          <p className="text-slate-500 text-sm">
            All administrative access is logged and monitored for security purposes
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminVerification;
